import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import "lenis/dist/lenis.css";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/landing/Nav";
import ColdSnapBanner from "@/components/landing/ColdSnapBanner";
import Hero from "@/components/landing/Hero";
import Marquee from "@/components/landing/Marquee";
import UnitFinder from "@/components/landing/UnitFinder";
import PriceList from "@/components/landing/PriceList";
import Reviews from "@/components/landing/Reviews";
import Booking from "@/components/landing/Booking";
import TrustBento from "@/components/landing/TrustBento";
import Chapters from "@/components/landing/Chapters";
import Faq from "@/components/landing/Faq";
import Footer from "@/components/landing/Footer";
import MobileCallBar from "@/components/landing/MobileCallBar";
import ExitIntentModal from "@/components/landing/ExitIntentModal";
import { findSuburbBySlug } from "@/data/suburbs";
import { TOPICS } from "@/data/topics";
import TopicPage from "@/components/landing/TopicPage";
import BathBreak from "@/components/landing/BathBreak";
import UnitPage from "@/pages/UnitPage";
import HelpTopics from "@/components/landing/HelpTopics";
import { initAnalytics, trackConversion, trackEvent, trackPageView } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CANONICAL_BASE = process.env.REACT_APP_CANONICAL_BASE;

function useSeo(suburb) {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = suburb
      ? `Hot Water Systems ${suburb.name} | Same or Next-Day Replacement, Upfront Prices Online | The Plumbing & Electrical Co.`
      : "Hot Water Systems Perth | Same-Day Swap-Outs, Upfront Prices Online | The Plumbing & Electrical Co.";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        suburb
          ? `Hot water system replacement, repairs & installs in ${suburb.name} WA. Upfront like-for-like swap-out prices online for 78+ gas & electric units. Same or next day service, lifetime labour warranty. Book online.`
          : "Perth's hot water system replacement & repair experts. Upfront like-for-like swap-out prices online for 78+ gas & electric units — Rheem, Rinnai, Dux, Vulcan, Thermann, Bosch. Book live availability online. Same or next day service, lifetime labour warranty."
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", suburb ? `${CANONICAL_BASE}/hot-water-systems-${suburb.slug}` : `${CANONICAL_BASE}/`);
    }
    trackPageView(window.location.pathname);
  }, [suburb]);
}

function Landing({ suburb = null }) {
  const [units, setUnits] = useState([]);
  const [notes, setNotes] = useState({});
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [photoId, setPhotoId] = useState(null);
  const location = useLocation();

  useSeo(suburb);

  useEffect(() => {
    if (!location.hash) return;
    const t = setTimeout(() => document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 400);
    return () => clearTimeout(t);
  }, [location.hash]);

  useEffect(() => {
    axios.get(`${API}/units`).then((res) => {
      setUnits(res.data.units);
      setNotes(res.data.notes || {});
    }).catch((e) => console.error("units fetch failed", e));
  }, []);

  const scrollTo = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const goBook = useCallback(() => {
    trackConversion("book_click");
    scrollTo("book");
  }, [scrollTo]);

  const bookUnit = useCallback((unit) => {
    setSelectedUnit(unit);
    trackEvent("select_unit", { unit_id: unit.id, unit_name: `${unit.brand} ${unit.model}` });
    trackConversion("book_click");
    scrollTo("book");
  }, [scrollTo]);

  return (
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
      <div className="App bg-white text-tpeco-ink font-body overflow-x-clip" data-testid="app-root">
        <ColdSnapBanner onBook={goBook} />
        <Nav onBook={goBook} />
        <main>
          <Hero suburb={suburb} onFindUnit={() => scrollTo("finder")} onBook={goBook} />
          <Marquee />
          <div className="lg:hidden">
            <BathBreak mobileTop />
          </div>
          <UnitFinder units={units} onBookUnit={bookUnit} onPhotoIdentified={setPhotoId} />
          <div className="hidden lg:block">
            <BathBreak />
          </div>
          <PriceList units={units} notes={notes} onBookUnit={bookUnit} />
          <HelpTopics />
          <Reviews suburb={suburb} />
          <Booking selectedUnit={selectedUnit} suburb={suburb} photoId={photoId} />
          <TrustBento />
          <Chapters />
          <Faq />
        </main>
        <Footer />
        <MobileCallBar onBook={goBook} />
        <ExitIntentModal onBook={goBook} />
        <Toaster position="top-center" richColors />
      </div>
    </ReactLenis>
  );
}

function SuburbPage() {
  const { slug } = useParams();
  const suburb = findSuburbBySlug(slug);
  if (!suburb) return <Navigate to="/" replace />;
  return <Landing key={suburb.slug} suburb={suburb} />;
}

function App() {
  useEffect(() => {
    initAnalytics();
    const onClick = (e) => {
      const a = e.target.closest?.("a[href^='tel:'],a[href^='sms:']");
      if (a) trackConversion(a.getAttribute("href").startsWith("sms") ? "sms_click" : "call_click", { link: a.getAttribute("href") });
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/units/:slug" element={<UnitPage />} />
        {TOPICS.map((t) => (
          <Route key={t.slug} path={`/${t.slug}`} element={<TopicPage topic={t} />} />
        ))}
        <Route path="/:slug" element={<SuburbPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
